import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUrlState } from '../hooks/useUrlState';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { sortCompare, makeToggleSort } from '../components/SortTh';
import { EmailStatusIndicator } from '../components/EmailEngagementPill';
import ErrorBoundary from '../components/ErrorBoundary';
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  FileText,
  Loader2,
  Plus,
  Receipt,
  Search,
  Send,
  X,
} from 'lucide-react';
import DeletedTicketsTab, { useDeletedTicketsCount } from '../components/tickets/DeletedTicketsTab';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../lib/load-failure';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import { getJobTypeLabel, jobTypeMatchesSearch, JOB_TYPE_EMPTY_LABEL } from '../constants/job-types';
import { fmtCurrency, parseMoney } from '../lib/display-labels';
import { formatTicketHours, hasContradictoryTimes } from '../lib/ticket-hours';
import { calendarDayString, formatCalendarDate } from '../lib/calendar-date';
import { readTicketDate } from '../lib/ticket-date';
import { unwrapArray } from '../lib/api-envelope';
import { isSalesRole } from '../lib/sales-access';

/**
 * The office ticket register, on the page layout gold standard.
 *
 * RE-SKIN ONLY, AND THIS SCREEN HOLDS NONE OF THE TICKET STATE MACHINE. Every
 * approve, send, reject, revert, delete and restore lives in
 * `pages/tickets/TicketDetail.tsx` (which owns the two-step office approve, the
 * `setQueryData` write-through and `CustomerEmailPromptModal`) or in
 * `components/tickets/DeletedTicketsTab.tsx` (the one restore mutation). This
 * file issues two GETs, maps them, filters, sorts and navigates. Nothing in it
 * writes, and nothing in it was moved.
 *
 * THE TALLIES ARE HONEST BECAUSE THE LISTS ARE COMPLETE. `GET /tickets` takes
 * `archived` and `jobId` only, applies no LIMIT and no OFFSET, and returns
 * every non-deleted ticket for the company ordered by creation — read out of
 * the API's `tickets.routes.ts`, not assumed. That is the Rate Sheets case
 * rather than the Quotes case: no endpoint returns these figures, so counting
 * loaded rows is the only answer available and it is not a second one. If a
 * page parameter is ever added to that route, every count on this screen
 * becomes a count of the current page and has to move to the server.
 *
 * WHAT CHANGED BEYOND PAINT, AND WHY EACH ONE HAD TO:
 *
 *   - The query gained `LOAD_FAILURE_QUERY_OPTIONS` and the register gained a
 *     third branch. It had two, and a dead API read "No tickets found" — the
 *     failure this repo has now found on nine screens.
 *   - An unrecognised status was rendered as **Draft** (`STATUS_CONFIG[s] ||
 *     STATUS_CONFIG.draft`). That is a confident claim about a row the screen
 *     could not classify, on a register where "Draft" means nobody has stood
 *     behind the work yet. It now reads `—` on slate with a transparent rail.
 *   - `fmtCurrency(total)` had the currency code appended by the call site, so
 *     an absent total rendered `— CAD`. The code is now passed to the
 *     formatter, which is what the sibling Deleted tab already did, so an
 *     absent total is a bare em dash and a real one carries `CA$`.
 *   - The approval mark beside the service type existed in two copies whose
 *     branch ORDER differed (the card tested `customer_approved` before
 *     `office_approved`, the table after). One component now, in the table's
 *     order. The two copies disagreed only for a ticket that is
 *     `office_approved` and carries `customer_approved_at`.
 *
 * WHAT DELIBERATELY DID NOT CHANGE: the filter predicates, the sort keys and
 * their comparator, the `?status=` seeding, the `?jobId=` chip, the search
 * predicate, the two-dataset split that keeps the tab counts stable, and the
 * status vocabulary — including this screen's own labels for the three legacy
 * aliases, which differ from `lib/ticket-status.ts` on purpose (see the note at
 * the head of that file). Folding the two together is a follow-up with its own
 * diff.
 *
 * THE DELETED TAB IS NOT RE-SKINNED HERE. It is a separate component with its
 * own query and its own restore mutation, and it renders its own panels; it
 * therefore replaces the register rather than sitting inside it. Bringing it
 * onto the register idiom means touching a mutation, which this change may not.
 */

/** Canonical + legacy statuses this screen can be handed, and their labels. */
type TicketStatusKey =
  | 'draft'
  | 'active'
  | 'submitted'
  | 'field_approved'
  | 'approved'
  | 'office_approved'
  | 'customer_approved'
  | 'customer_rejected'
  | 'rejected'
  | 'invoiced'
  | 'archived';

/**
 * The register's status pill and row rail — the Quotes / Rate Sheets badge,
 * dark ink on a saturated signal fill.
 *
 * ELEVEN KEYS, FIVE SIGNAL HUES, GROUPED BY WHAT THE OFFICE DOES NEXT. The
 * palette has no purple and no orange, so the standard's instruction applies:
 * share the nearest hue and let the label tell them apart, rather than minting
 * a colour that has had no light-mode or contrast review and means something on
 * one page only.
 *
 *   slate  nothing is in play — draft (and its two legacy spellings), archived
 *   amber  the OFFICE must act — field_approved
 *   blue   out with the customer, waiting — office_approved (and legacy
 *          `approved`)
 *   green  no further office action — customer_approved, invoiced
 *   red    it came back — rejected, customer_rejected
 *
 * The three legacy keys carry the labels this screen has always given them.
 * `submitted` reads "Draft" here and "Field Approved" in `lib/ticket-status.ts`;
 * that disagreement is pre-existing, documented in that file, and is not settled
 * by a re-skin.
 *
 * A VALUE OUTSIDE THIS MAP READS `—` ON SLATE WITH A TRANSPARENT RAIL. It used
 * to fall back to Draft's config, which on a ticket register is a claim that
 * nobody has submitted work that may well have been invoiced.
 */
const STATUS_BADGE: Record<TicketStatusKey, { label: string; fill: string; rail: string }> = {
  draft:             { label: 'Draft',             fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  active:            { label: 'Draft',             fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  submitted:         { label: 'Draft',             fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  field_approved:    { label: 'Field Approved',    fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  approved:          { label: 'Office Approved',   fill: 'bg-fi-signal-blue',  rail: 'border-l-fi-blue' },
  office_approved:   { label: 'Office Approved',   fill: 'bg-fi-signal-blue',  rail: 'border-l-fi-blue' },
  customer_approved: { label: 'Customer Approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  customer_rejected: { label: 'Customer Rejected', fill: 'bg-fi-signal-red',   rail: 'border-l-fi-red' },
  rejected:          { label: 'Rejected',          fill: 'bg-fi-signal-red',   rail: 'border-l-fi-red' },
  invoiced:          { label: 'Invoiced',          fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  archived:          { label: 'Archived',          fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};

const UNKNOWN_STATUS = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

const statusSkin = (status: string | null | undefined) =>
  (status && STATUS_BADGE[status as TicketStatusKey]) || UNKNOWN_STATUS;

function TicketStatusBadge({ status }: { status: string | null | undefined }) {
  const skin = statusSkin(status);
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/**
 * The approval mark beside the service type — WHICH DESK HAS SIGNED, not which
 * state the ticket is in.
 *
 * It is neutral-outlined on purpose, and that is a change. It used to be a
 * green or red tinted chip, which put a second status-coloured thing on a row
 * whose status badge already carries the hue — and in the one arm where the
 * colour said "alarm", the status is `customer_rejected`, so the badge two
 * columns along is already red. The tick and the cross carry the rest.
 *
 * The branch order is the desktop table's, which was the office-facing copy.
 * The narrow-viewport card carried the same four arms with
 * `customer_approved` tested before `office_approved`; the two answers differ
 * only for a ticket that is `office_approved` AND carries
 * `customer_approved_at`, and the table's reading — name the desk the ticket is
 * sitting on — is the one the register wants.
 */
function ApprovalMark({ status, customerApprovedAt }: { status: string; customerApprovedAt?: string | null }) {
  const MARK =
    'whitespace-nowrap rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-1.5 py-0.5 text-[10px] font-semibold text-fieldiq-text';
  if (status === 'customer_rejected') return <span className={MARK}>Customer ✗</span>;
  if (status === 'field_approved') return <span className={MARK}>Field ✓</span>;
  if (status === 'office_approved' || status === 'approved') return <span className={MARK}>Office ✓</span>;
  if (status === 'customer_approved' || customerApprovedAt) return <span className={MARK}>Customer ✓</span>;
  return null;
}

/**
 * A sortable heading cell in the handoff palette.
 *
 * LOCAL PAINT, SHARED LOGIC — the split JobsScreen and QuotesScreen document.
 * `sortCompare` and `makeToggleSort` are imported, so there is one sort
 * implementation and not two; only the colours differ, because the shared
 * `SortTh` is spelled in the older surface names and re-skinning it would
 * repaint the twenty-odd screens still rendering it. This is now the third call
 * site for this paint and extracting it is owed work — with its own diff, where
 * a reviewer can see every call site converge at once.
 */
interface SortHeadProps {
  label: string;
  field: string;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  className?: string;
}

function SortHead({ label, field, sortBy, sortDir, onSort, className = '' }: SortHeadProps) {
  const active = sortBy === field;
  return (
    <th
      scope="col"
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => onSort(field)}
      className={`group/th cursor-pointer select-none whitespace-nowrap px-4 py-3 text-left transition hover:text-white ${
        active ? 'text-white' : 'text-fieldiq-muted'
      } ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="inline-flex flex-col leading-none">
          <ChevronUp
            className={`size-3 ${
              active && sortDir === 'asc' ? 'text-fi-amber' : 'text-fieldiq-muted/40 group-hover/th:text-fieldiq-muted/60'
            }`}
          />
          <ChevronDown
            className={`-mt-0.5 size-3 ${
              active && sortDir === 'desc' ? 'text-fi-amber' : 'text-fieldiq-muted/40 group-hover/th:text-fieldiq-muted/60'
            }`}
          />
        </span>
      </span>
    </th>
  );
}

// --- Types ---
interface LineItem {
  id: string;
  description?: string;
  line_item_type: string;
  unit?: string;
  rate?: string;
  quantity?: string;
  total?: string;
}

interface Ticket {
  id: string;
  ticketNumber?: string;
  jobId: string;
  jobNumber?: string;
  status: string;
  date: string;
  /**
   * The API's ticket date (api#1329): the finish day when the tenant dates
   * tickets by it, else work_date. Drives this list's Date column, its sort and
   * its date search. `date` stays the arrival day, the mirror of TicketDetail.
   */
  ticketDate: string | null;
  /** Arrival / finish calendar days, shown under the date when they differ. */
  arrivalDate: string | null;
  customer: string;
  serviceType?: string;
  // Server-computed and PRE-FORMATTED for display ('8.00'), null when the server
  // has no answer — same shape TicketDetail carries, so the two screens can
  // never disagree about the same row. Never a number: a number would tempt a
  // `|| 0` back into existence.
  hours: string | null;
  hoursContradictory?: boolean;
  crewCount?: number;
  total: number | null;
  actualMetres?: number | null;
  plannedMetres?: number;
  crew?: { name: string; role: string; initials: string }[];
  equipment?: { name: string; type: string; serial_number?: string; status?: string }[];
  lineItems?: LineItem[];
  subtotal?: number | null;
  tax?: number | null;
  taxRate?: number;
  taxRate1Label?: string;
  taxRate1Amount?: number | null;
  taxRate2Label?: string;
  taxRate2Amount?: number | null;
  customerContactName?: string;
  customerContactPhone?: string;
  customerContactEmail?: string;
  customerAddress?: string;
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
  lsd?: string;
  wellName?: string;
  afeNumber?: string;
  rigName?: string;
  createdBy?: string;
  archivedAt?: string | null;
  invoiceNumber?: string | null;
  sentToCustomerAt?: string | null;
  customerApprovedAt?: string | null;
  customerApprovedByName?: string | null;
  fieldApprovedAt?: string | null;
  customerRejectionReason?: string | null;
  customerEmailStatus?: 'not_sent' | 'pending' | 'sent' | 'failed' | 'bounced' | 'delivered' | 'opened' | 'clicked' | 'complained';
  customerEmailLastAttemptAt?: string | null;
  customerEmailLastError?: string | null;
  customerEmailProviderMessageId?: string | null;
  customerEmailDeliveredAt?: string | null;
  customerEmailOpenCount?: number;
  customerEmailLastOpenedAt?: string | null;
  customerEmailClickCount?: number;
  customerEmailLastClickedAt?: string | null;
  customerEmailComplainedAt?: string | null;
  approvalUrl?: string | null;
  needsCustomerEmail?: boolean;
}

/**
 * The chips, and what each one asks of the loaded rows.
 *
 * CHIPS, NOT TABS, for six of the seven: each narrows a list of tickets by
 * status and carries its count. `deleted` is the exception and is genuinely a
 * different question over a different dataset — it is left in this row because
 * splitting it into a tab strip changes the control the office already uses,
 * which a re-skin may not do. Flagged, not fixed.
 */
const FILTER_LABELS: Record<string, string> = {
  open: 'Open',
  draft: 'Draft',
  approved: 'Approved',
  invoiced: 'Invoiced',
  rejected: 'Rejected',
  archived: 'Archived',
  deleted: 'Deleted',
};

/**
 * A ticket's work date is a CALENDAR DAY ('2026-07-14'), not an instant.
 * `new Date()` parsed it as UTC midnight, so every date in this list rendered a
 * day early in Alberta — a ticket worked on Jul 14 said "Jul 13, 2026".
 * formatCalendarDate builds the day locally and returns null when there is no
 * day, which is the em dash rather than today's date.
 */
function fmtDate(d: any) {
  return formatCalendarDate(d) ?? '—';
}

/**
 * Deliberately a line-for-line mirror of mapTicket() in
 * src/pages/tickets/TicketDetail.tsx (fixed in PR #301) — EXCEPT `ticketDate`
 * and `arrivalDate`, which only the list has: the list is dated by the API's
 * ticket date (api#1329) and the detail deliberately stays on work_date. The list and the detail
 * read the same row off the same endpoint, so any divergence here is a screen
 * that contradicts the screen one click away — which is exactly what this file
 * did: detail rendered '—' where the list rendered '$0.00' and '0h'.
 *
 * SEVERAL KEYS BELOW ARE NOT ON THE LIST PAYLOAD AT ALL, and they are left
 * alone. `GET /tickets` is `SELECT t.*, j.job_number` plus a last-editor name,
 * so it carries the `tickets` columns and nothing else: the contact block
 * (`customer_contact_*`, `customer_address`), the company block
 * (`company_name/address/phone`), `created_by_name` / `submitted_by_name`,
 * `approval_url`, `line_items` and `equipment` are all joins that only
 * `GET /tickets/:id` performs. They resolve to null here, and nothing this
 * screen renders reads them. Reported rather than pruned — the mirror with
 * TicketDetail's mapper is the property worth keeping, and trimming one side
 * of it is how the two drifted apart the first time.
 */
function mapTicket(t: any): Ticket {
  // Money the server did not send stays null so fmtCurrency renders '—'. A
  // priced-at-zero ticket and an unpriced one are different facts on an invoice.
  // `??` not `||`, so a real 0 is not mistaken for an absent snake_case key and
  // replaced by the camelCase one.
  const taxRate1Amount = parseMoney(t.tax_rate_1_amount ?? t.taxRate1Amount);
  const taxRate2Amount = parseMoney(t.tax_rate_2_amount ?? t.taxRate2Amount);
  const subtotal = parseMoney(t.subtotal);
  const totalAmount = parseMoney(
    t.total_amount ?? t.totalAmount ?? t.total_price ?? t.totalPrice,
  );
  // Falling back to the sum is only meaningful if at least one part arrived;
  // summing three nulls would invent a $0.00 total.
  const derivedTotal =
    subtotal === null && taxRate1Amount === null && taxRate2Amount === null
      ? null
      : (subtotal ?? 0) + (taxRate1Amount ?? 0) + (taxRate2Amount ?? 0);
  const tax =
    taxRate1Amount === null && taxRate2Amount === null
      ? null
      : (taxRate1Amount ?? 0) + (taxRate2Amount ?? 0);
  return {
    id: t.id,
    ticketNumber: t.ticket_number || t.ticketNumber,
    jobId: t.job_id || t.jobId || '',
    jobNumber: t.job_number || t.jobNumber,
    status: t.status || 'draft',
    date: t.work_date || t.workDate || t.created_at || t.createdAt || '',
    // Read, never derived: see lib/ticket-date.ts. created_at is the list's
    // existing last resort for a ticket with no work day yet, kept as it was.
    ticketDate: readTicketDate(t) ?? (t.created_at || t.createdAt || null),
    arrivalDate: t.work_date || t.workDate || null,
    /**
     * ABSENT IS AN EM DASH, NOT "Unknown".
     *
     * "Unknown" is a fallback string dressed as data: it names the customer as
     * a thing called Unknown, and it reads the same whether the ticket has no
     * customer, the tenant has not filled one in, or the list endpoint simply
     * did not send one. Only the last of those is true here.
     *
     * WHAT WAS ABSENT, measured on Smith 2026-09-21 — and it was a property of
     * the LIST endpoint, not of the ticket. `GET /tickets` selected `t.*` joined
     * to `jobs` alone, so it returned the raw `tickets.customer_name` snapshot
     * column, NULL on FT-00063 and FT-00064, while `GET /tickets/:id` and its
     * siblings did `COALESCE(t.customer_name, cust.name)` against a joined
     * `customers` row. The customer existed and was live: the detail screen
     * rendered "cenovus test" for the same ticket the list called Unknown.
     *
     * api#777 CLOSED THAT GAP — the list query now carries the same COALESCE,
     * and there are SIX sibling queries doing it, not the four counted here
     * when the gap was open.
     *
     * THE DASH STAYS, AND THE REASON IS NOT THE ONE ABOVE ANY MORE. It was "the
     * server did not send it"; it is now "the server has nothing to send". A
     * ticket whose snapshot is NULL and whose customer row is gone or unset
     * still resolves to nothing, and that case renders a dash for the same
     * reason it always should have: "Unknown" is a fallback string dressed as
     * data. Nothing here changes with the fix, which is the point — a screen
     * that renders absence honestly does not need editing when the absence
     * becomes rarer.
     *
     * STILL NOT RESOLVED CLIENT-SIDE, and that outlives the fix too: joining
     * the customers list here would be a second answer to a question the API
     * now answers correctly.
     */
    customer: t.customer_name || t.customerName || '—',
    serviceType: t.job_type || t.jobType || '',
    // Server-computed. null (contradictory times) must not read as "0 hrs".
    hours: formatTicketHours(t.total_hours ?? t.totalHours),
    hoursContradictory: hasContradictoryTimes(
      t.work_date ?? t.workDate,
      t.end_date ?? t.endDate,
      t.start_time ?? t.startTime,
      t.end_time ?? t.endTime,
    ),
    crewCount: 0,
    total: totalAmount ?? derivedTotal,
    // A billable quantity, so absent is an under-bill, not a zero. parseMoney is
    // the shared absence-preserving coercion — its name says money, its contract
    // is "coerce, keep null null". fmtQty is the wrong tool: it returns display
    // copy, and nothing on this screen renders metres.
    actualMetres: parseMoney(t.actual_metres_run ?? t.actualMetresRun),
    subtotal,
    tax,
    taxRate1Label: t.tax_rate_1_label || t.taxRate1Label || null,
    taxRate1Amount,
    taxRate2Label: t.tax_rate_2_label || t.taxRate2Label || null,
    taxRate2Amount,
    lineItems: t.lineItems || t.line_items || undefined,
    equipment: t.equipment || undefined,
    customerContactName: t.customer_contact_name || t.customerContactName || null,
    customerContactPhone: t.customer_contact_phone || t.customerContactPhone || null,
    customerContactEmail: t.customer_contact_email || t.customerContactEmail || null,
    customerAddress: t.customer_address || t.customerAddress || null,
    companyName: t.company_name || t.companyName || null,
    companyAddress: t.company_address || t.companyAddress || null,
    companyPhone: t.company_phone || t.companyPhone || null,
    lsd: t.lsd || t.well_id_lsd || t.wellIdLsd || null,
    wellName: t.well_name || t.wellName || null,
    afeNumber: t.afe_number || t.afeNumber || null,
    rigName: t.rig_name || t.rigName || null,
    createdBy: t.created_by_name || t.createdByName || t.submitted_by_name || null,
    archivedAt: t.archived_at || t.archivedAt || null,
    invoiceNumber: t.invoice_number || t.invoiceNumber || null,
    sentToCustomerAt: t.sent_to_customer_at || t.sentToCustomerAt || null,
    customerApprovedAt: t.customer_approved_at || t.customerApprovedAt || null,
    customerApprovedByName: t.customer_approved_by_name || t.customerApprovedByName || null,
    fieldApprovedAt: t.field_approved_at || t.fieldApprovedAt || null,
    customerRejectionReason: t.customer_rejection_reason || t.customerRejectionReason || null,
    customerEmailStatus: t.customer_email_status || t.customerEmailStatus || 'not_sent',
    customerEmailLastAttemptAt: t.customer_email_last_attempt_at || t.customerEmailLastAttemptAt || null,
    customerEmailLastError: t.customer_email_last_error || t.customerEmailLastError || null,
    customerEmailProviderMessageId: t.customer_email_provider_message_id || t.customerEmailProviderMessageId || null,
    customerEmailDeliveredAt: t.customer_email_delivered_at || t.customerEmailDeliveredAt || null,
    customerEmailOpenCount: parseInt(t.customer_email_open_count || t.customerEmailOpenCount) || 0,
    customerEmailLastOpenedAt: t.customer_email_last_opened_at || t.customerEmailLastOpenedAt || null,
    customerEmailClickCount: parseInt(t.customer_email_click_count || t.customerEmailClickCount) || 0,
    customerEmailLastClickedAt: t.customer_email_last_clicked_at || t.customerEmailLastClickedAt || null,
    customerEmailComplainedAt: t.customer_email_complained_at || t.customerEmailComplainedAt || null,
    approvalUrl: t.approval_url || t.approvalUrl || null,
    needsCustomerEmail: t.needs_customer_email ?? t.needsCustomerEmail ?? false,
  };
}

/**
 * The three states of a ticket's hours, in the same words TicketDetail uses.
 *
 * Not a formatter — formatTicketHours/hasContradictoryTimes did that in the
 * mapper. This only picks which of the three the row is in, and it exists as one
 * component because the desktop table and the mobile card below both render it;
 * two copies is how the list and the detail drifted apart in the first place.
 *
 * A real 0 arrives here as the string '0.00', which is truthy, so a ticket that
 * genuinely booked no hours still reads "0.00 hrs" — the absent case is null.
 *
 * THE CONTRADICTION LINE KEEPS ITS ALARM AND LOSES ITS INK COLOUR. It was
 * `text-red-400`, which is 3.0 on a white panel once the app is in light mode —
 * the warning is simply not there, and nothing reports it because the class is
 * in the DOM and the build is clean. The wash carries the hue and the ink is
 * the body token, which is the treatment CLAUDE.md prescribes for red on a
 * theme-following surface; `text-fieldiq-danger` is NOT the fix, it measures
 * 3.44 on its own 15% wash.
 */
function HoursDisplay({ hours, contradictory }: { hours: string | null; contradictory?: boolean }) {
  if (hours) return <span className="text-fieldiq-text">{hours} hrs</span>;
  if (contradictory) {
    return (
      <span className="inline-flex items-center rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/15 px-2 py-0.5 text-xs font-semibold text-fieldiq-text">
        Times contradict — finish before arrival
      </span>
    );
  }
  return <span className="text-fieldiq-muted">Not calculated</span>;
}

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function TicketsScreen() {
  const navigate = useNavigate();
  const { company, user } = useAuth();
  const isSales = isSalesRole(user?.role); // sales reads tickets, creates none (api#1032)
  const [searchParams, setSearchParams] = useSearchParams();
  // Search, chip and sort live in the URL (useUrlState), so Back from a ticket
  // returns to the same results. `status` was already the inbound key, and an
  // inbound link may carry a ticket STATUS rather than a chip key, so it is
  // mapped here; an unknown value falls back to the default chip.
  const [statusParam, setFilter] = useUrlState<string>('status', 'open');
  const [search, setSearch] = useUrlState<string>('q', '');
  const [sortBy, setSortBy] = useUrlState<string>('sort', 'date');
  const [sortDir, setSortDir] = useUrlState<'asc' | 'desc'>('dir', 'desc');
  const STATUS_TO_FILTER: Record<string, string> = {
    field_approved: 'approved',
    office_approved: 'approved',
    customer_approved: 'approved',
    customer_rejected: 'rejected',
  };
  const mappedFilter = STATUS_TO_FILTER[statusParam] ?? statusParam;
  const filter = Object.prototype.hasOwnProperty.call(FILTER_LABELS, mappedFilter) ? mappedFilter : 'open';
  const showArchived = filter === 'archived';

  const jobIdParam = searchParams.get('jobId') || '';
  const currency = company?.country === 'CA' ? 'CAD' : 'USD';
  const { count: deletedCount, settled: deletedArrived } = useDeletedTicketsCount();

  /**
   * Two independent datasets so tab counts never collapse to the active tab.
   * Non-archived feeds open/draft/approved/invoiced/rejected; archived feeds its
   * own list + count. Each is fetched once and cached, so flipping tabs reads
   * cache rather than re-fetching.
   *
   * A FAILED LIST IS NOT AN EMPTY DESK. Both queries carried `retry: false` and
   * were destructured `{ data, isLoading }`, which is two branches where there
   * are three: every failure arrived as `undefined` data, `|| []` turned that
   * into an empty list, and the screen said "No tickets found" — the reassuring
   * one. `LOAD_FAILURE_QUERY_OPTIONS` stops a 401/403 being retried (a refusal
   * is an answer, not a flake) and keeps the failure where the screen can read
   * it; `queryLoadFailure` reads the PAUSED state as well as the settled one,
   * because a query waiting to retry a 5xx sits at `isLoading === false` with a
   * null `error`.
   */
  const activeQuery = useQuery({
    queryKey: ['tickets', false, jobIdParam || undefined],
    queryFn: async () => {
      const res = await api.tickets.list(jobIdParam ? { jobId: jobIdParam } : undefined);
      return unwrapArray<any>(res.data);
    },
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const archivedQuery = useQuery({
    queryKey: ['tickets', true, jobIdParam || undefined],
    queryFn: async () => {
      const res = await api.tickets.list({ archived: true, ...(jobIdParam ? { jobId: jobIdParam } : {}) });
      return unwrapArray<any>(res.data);
    },
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const { data: activeData, isLoading: activeLoading } = activeQuery;
  const { data: archivedData, isLoading: archivedLoading } = archivedQuery;
  const activeFailure = queryLoadFailure(activeQuery);
  const archivedFailure = queryLoadFailure(archivedQuery);

  // Map API data to Ticket interface
  const activeTickets: Ticket[] = useMemo(() => (activeData || []).map((t: any) => mapTicket(t)), [activeData]);
  const archivedTickets: Ticket[] = useMemo(() => (archivedData || []).map((t: any) => mapTicket(t)), [archivedData]);

  // The list rendered below reflects the active tab; counts are derived from the full datasets above.
  const tickets: Ticket[] = showArchived ? archivedTickets : activeTickets;
  const ticketsLoading = showArchived ? archivedLoading : activeLoading;
  const ticketsFailure = showArchived ? archivedFailure : activeFailure;
  const ticketsQuery = showArchived ? archivedQuery : activeQuery;

  /**
   * HAS THE LIST ACTUALLY ARRIVED? One flag per dataset — the stat strip and
   * the five open-list chips read the first, the Archived chip reads the second,
   * and the register's scope line reads whichever feeds the tab in view.
   *
   * `isLoading` IS NOT THAT QUESTION. A query that has failed and is waiting to
   * retry sits at `status: 'pending'`, `fetchStatus: 'paused'`, and React
   * Query's `isLoading` is pending AND fetching — so while it is paused
   * `isLoading` is FALSE. A figure gated on it un-masks over `data` that is
   * still `undefined`, `|| []` makes that an empty list, and the band renders a
   * row of confident zeros directly above a panel reading "Couldn't load field
   * tickets". That is the em-dash rule one layer up: a zero is a statement about
   * the company, and the only thing that had happened was a failed request.
   *
   * `data !== undefined` rather than `.length` — an empty array is a real answer
   * ("this tenant has no tickets") and has to stay distinguishable from no
   * answer at all.
   */
  const activeArrived = !activeLoading && !activeFailure && activeData !== undefined;
  const archivedArrived = !archivedLoading && !archivedFailure && archivedData !== undefined;
  const listArrived = showArchived ? archivedArrived : activeArrived;

  /* IS IT STILL IN FLIGHT? A DIFFERENT QUESTION FROM "HAS IT ARRIVED", AND THE
   * BAND'S PULSE IS THE ONE THAT NEEDS IT.
   *
   * `!activeArrived` is true while the read is in flight AND true forever after
   * it fails, so the flag that draws a pulse where each figure goes could not
   * tell the two apart. A failed read HAS an answer — "we could not read this" —
   * and the band's spelling for it is an em dash. A pulse says the opposite: that
   * a number is on its way. So the strip animated indefinitely above a register
   * that had already said "Couldn't load field tickets".
   *
   * This is the docblock above, one state further along. There the strip
   * un-masked too early and printed a row of confident zeros; here it never
   * un-masked and printed a confident "not yet". Both let the band claim
   * something about the company from a fact about the network.
   *
   * THE FIGURES HAD TO MOVE WITH IT, and flipping the flag alone would have
   * restored those zeros exactly. `stageCounts` counts `activeTickets`, which is
   * `activeData || []`, so on a failed read every stage is a real, honest `0` —
   * and `displayValue(0)` is `0`, not a dash. Each value is therefore gated on
   * `activeArrived` and passes `null` when the list never came, which is the only
   * spelling `displayValue` turns into an em dash. */
  const activePending = !activeArrived && !activeFailure;

  // Derive job label for the filter chip from the first matching ticket
  const jobFilterLabel = useMemo(() => {
    if (!jobIdParam) return '';
    const match = activeTickets.find(t => t.jobId === jobIdParam);
    return match?.jobNumber ? `Job #${match.jobNumber}` : `Job ${jobIdParam.slice(0, 8)}`;
  }, [jobIdParam, activeTickets]);

  const clearJobFilter = () => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('jobId');
      return next;
    });
  };

  // Search + filter
  const filteredTickets = useMemo(() => {
    let result = tickets;

    // Job ID filter (client-side fallback)
    if (jobIdParam) {
      result = result.filter(t => t.jobId === jobIdParam);
    }

    // Text search
    if (search) {
      const s = search.toLowerCase();
      result = result.filter((t) => {
        const num = (t.ticketNumber || t.id || '').toLowerCase();
        const cust = (t.customer || '').toLowerCase();
        const jobNum = (t.jobNumber || '').toLowerCase();
        // serviceType holds the canonical job_type code; match on the code AND
        // the label so searching the text on screen ("Liner & Tie-Back") works.
        // The date matches as the API's ticket date, in both spellings a
        // reader might type: the ISO day and the day as this list renders it.
        const day = (t.ticketDate || '').toLowerCase();
        const dayShown = t.ticketDate ? fmtDate(t.ticketDate).toLowerCase() : '';
        return num.includes(s) || cust.includes(s) || jobTypeMatchesSearch(t.serviceType, search) || jobNum.includes(s)
          || (day !== '' && day.includes(s)) || (dayShown !== '' && dayShown.includes(s));
      });
    }

    // Status filter
    if (filter === 'open') {
      // customer_rejected stays in Open — it needs active office work (revise & resend)
      result = result.filter(t => ['draft', 'field_approved', 'office_approved', 'customer_approved', 'customer_rejected'].includes(t.status) && !t.invoiceNumber);
    } else if (filter === 'approved') {
      result = result.filter(t => t.status === 'field_approved' || t.status === 'office_approved' || t.status === 'customer_approved');
    } else if (filter === 'invoiced') {
      result = result.filter(t => t.status === 'invoiced' || t.invoiceNumber);
    } else if (filter === 'rejected') {
      // Fold customer rejections in alongside office rejections
      result = result.filter(t => t.status === 'rejected' || t.status === 'customer_rejected');
    } else if (filter === 'archived') {
      // `tickets` already resolves to the archived dataset on this tab; no extra status filter needed
    } else if (filter !== 'all') {
      result = result.filter(t => t.status === filter);
    }

    return result;
  }, [tickets, filter, search, jobIdParam]);

  const toggleSort = makeToggleSort(setSortBy, setSortDir, sortBy);
  const sortedTickets = useMemo(() => {
    return [...filteredTickets].sort((a, b) => {
      // The Date column sorts by the API's ticket date. The URL key stays
      // `date` so existing links (?sort=date) keep working.
      const key = sortBy === 'date' ? 'ticketDate' : sortBy;
      const aVal = (a as any)[key];
      const bVal = (b as any)[key];
      return sortCompare(aVal, bVal, sortDir);
    });
  }, [filteredTickets, sortBy, sortDir]);

  /**
   * Counts for the chips and figures for the stat strip, from the two loaded
   * lists. Both ignore the search box and the chips themselves, exactly as the
   * reference pages' strips do: they describe the desk, not the current
   * narrowing, and a count that moved as you typed would be a fourth thing on
   * screen answering the same question as the register's own scope line.
   *
   * A TALLY OVER LOADED ROWS IS A REAL NUMBER, so a status with none of them
   * reads `0` and not a gap. The em dash is reserved for a figure that did not
   * arrive, and while a list is in flight nothing is claimed at all — its chips
   * render no number and its stats pulse.
   *
   * `deleted` comes from a THIRD query, inside `useDeletedTicketsCount`, and it
   * now obeys the same rule as the other two. The hook used to coalesce an
   * absent list to `0`, so a refused or failed deleted read said "no deleted
   * tickets" — a statement about the data assembled out of a failure to fetch
   * it. It returns `{ count, settled }` instead, `count` stays `undefined` when
   * the read did not arrive, and `displayValue` renders that as the absent
   * marker. A real empty list is still a real `0`.
   */
  const filterCounts = useMemo(() => ({
    open: activeTickets.filter(t => ['draft', 'field_approved', 'office_approved', 'customer_approved', 'customer_rejected'].includes(t.status) && !t.invoiceNumber).length,
    draft: activeTickets.filter(t => t.status === 'draft').length,
    approved: activeTickets.filter(t => t.status === 'field_approved' || t.status === 'office_approved' || t.status === 'customer_approved').length,
    invoiced: activeTickets.filter(t => t.status === 'invoiced' || t.invoiceNumber).length,
    rejected: activeTickets.filter(t => t.status === 'rejected' || t.status === 'customer_rejected').length,
    archived: archivedTickets.length,
    deleted: deletedCount,
  }), [activeTickets, archivedTickets, deletedCount]);

  /** The stat strip's four figures — the stages of one ticket's life. */
  const stageCounts = useMemo(() => ({
    draft: activeTickets.filter(t => ['draft', 'active', 'submitted'].includes(t.status)).length,
    fieldApproved: activeTickets.filter(t => t.status === 'field_approved').length,
    withClient: activeTickets.filter(t => t.status === 'office_approved' || t.status === 'approved').length,
    invoiced: activeTickets.filter(t => t.status === 'invoiced' || t.invoiceNumber).length,
  }), [activeTickets]);

  const openTicket = (id: string) => navigate(`/tickets/${id}`);

  /**
   * Enter and Space open a row — but ONLY when the row itself has focus.
   *
   * No explicit `role="button"`: an email-status mark rides inside the client
   * cell today and nothing on the row is focusable, but the role makes every
   * child presentational, and this is the register that gains a per-row control
   * the moment the office wants one. The guard costs nothing and is what stops
   * a key aimed at an inner control being swallowed by the row.
   */
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openTicket(id);
    }
  };

  const chipCount = (key: string): React.ReactNode => {
    // `deleted` used to hard-code `true` here, which was only safe while the
    // hook could not return "no answer" — it always had a number, because it
    // invented one. Now it can, so this reads the query's own settled state
    // like the other two.
    const arrived = key === 'archived' ? archivedArrived : key === 'deleted' ? deletedArrived : activeArrived;
    if (!arrived) return null;
    return <span className="tabular-nums">{displayValue((filterCounts as any)[key])}</span>;
  };

  return (
    <ErrorBoundary>
    <div data-testid="tickets-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global branded bar. Pages never do. */}
      <PageHeaderBand
        eyebrow="Casing operations"
        title="Field Tickets"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          isSales ? undefined : <button
            type="button"
            onClick={() => navigate('/tickets/new')}
            className="fi-action fi-action--standard fi-action--primary"
          >
            <Plus className="size-4" aria-hidden="true" />
            New Ticket
          </button>
        }
        /* Four figures, all of them tallies of the non-archived list already
           loaded above — no second query, and none of them recomputed from
           anything the API states directly. They are the stages of a ticket's
           life in order, company-wide, and they do NOT follow the chips or the
           search box.

           THE THIRD LABEL IS SIX CHARACTERS ON PURPOSE. The hero circle's edge
           crosses the third stat column between 1024 and 1048 wide, and the
           labels swept there run to about 70px. "With Client" would not have
           fitted that and would have needed the sweep re-run; "Client" with the
           sub-line doing the explaining does not.

           `Invoiced` counts a status OR an invoice number, matching the chip of
           the same name — the two must not disagree on one screen. */
        stats={[
          {
            icon: <FileText className="size-4" aria-hidden="true" />,
            label: 'Drafts',
            sub: 'Not yet submitted',
            tone: 'neutral',
            loading: activePending,
            value: displayValue(activeArrived ? stageCounts.draft : null),
          },
          {
            icon: <Clock className="size-4" aria-hidden="true" />,
            label: 'To Approve',
            sub: 'Waiting on the office',
            tone: 'accent',
            loading: activePending,
            value: displayValue(activeArrived ? stageCounts.fieldApproved : null),
          },
          {
            icon: <Send className="size-4" aria-hidden="true" />,
            label: 'Client',
            sub: 'Out for signature',
            tone: 'info',
            loading: activePending,
            value: displayValue(activeArrived ? stageCounts.withClient : null),
          },
          {
            icon: <Receipt className="size-4" aria-hidden="true" />,
            label: 'Invoiced',
            sub: 'Billed',
            tone: 'success',
            loading: activePending,
            value: displayValue(activeArrived ? stageCounts.invoiced : null),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* Chips and search — in the body, never on the band. */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
              {Object.keys(FILTER_LABELS).map((f) => {
                const active = filter === f;
                return (
                  <button
                    key={f}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setFilter(f)}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active
                        ? 'bg-fi-amber text-fi-amber-ink'
                        : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                    }`}
                  >
                    {FILTER_LABELS[f]}
                    {/* No count is claimed while the list is in flight, and a
                        real zero is shown once it has arrived — a chip that
                        hides its own zero, which is what this row used to do,
                        cannot be told apart from one whose count never loaded.
                        Full ink on the active chip: the 70% ink the first
                        re-skins used measures 3.7 on amber, under the floor. */}
                    {chipCount(f)}
                  </button>
                );
              })}
            </div>

            <div className="relative lg:w-96">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by ticket #, client, job #, service type, date..."
                aria-label="Search field tickets"
                className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-10 pr-8 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2"
                >
                  <X className="size-4 text-fieldiq-muted" />
                </button>
              )}
            </div>
          </div>

          {/* The job scope chip, when the register was entered from a job. */}
          {jobIdParam && jobFilterLabel && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-fi-pill border border-fi-amber/40 bg-fi-amber/15 px-3 py-1.5 text-sm font-bold text-fieldiq-text">
                {jobFilterLabel}
                <button
                  type="button"
                  onClick={clearJobFilter}
                  aria-label={`Clear ${jobFilterLabel} filter`}
                  className="ml-0.5 text-fieldiq-muted transition hover:text-fieldiq-text"
                >
                  <X size={14} />
                </button>
              </span>
            </div>
          )}

          {/* The Deleted tab is a separate component with its own query and its
              own restore mutation, and it draws its own panels. It replaces the
              register rather than sitting inside it; bringing it onto this
              idiom means touching that mutation, which this change may not. */}
          {filter === 'deleted' ? (
            <DeletedTicketsTab currency={currency} search={search} />
          ) : (
            <section
              data-testid="ticket-register"
              aria-labelledby="ticket-register-title"
              className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
            >
              {/* The header line carries SCOPE — how much of the desk's work
                  this listing is currently showing. That is something the
                  reader would otherwise get wrong after a search, which is what
                  earns a figure a place here. It is withheld until the list has
                  arrived, so a failed load cannot present as "0 of 0". */}
              <header className="flex items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
                <div>
                  <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Ticket register</p>
                  <h2 id="ticket-register-title" className="mt-1 text-base font-extrabold tracking-tight text-white">
                    {showArchived ? 'Archived tickets' : 'Work booked and billed'}
                  </h2>
                </div>
                {listArrived && (
                  <p className="text-sm tabular-nums text-fieldiq-muted">
                    {sortedTickets.length} of {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
                  </p>
                )}
              </header>

              {ticketsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="size-6 animate-spin text-fieldiq-muted" />
                </div>
              ) : ticketsFailure ? (
                /* The third branch, where the empty state would have been. No
                   `allowed` is passed: `GET /tickets` admits admin, office
                   staff, supervisor, dispatcher and field worker, so naming who
                   may read it would be a guess about which of them the reader
                   is not. */
                <LoadFailureNotice
                  error={queryFailureError(ticketsQuery)}
                  kind={ticketsFailure}
                  subject="field tickets"
                />
              ) : sortedTickets.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <FileText className="mx-auto mb-3 size-10 text-fieldiq-muted" aria-hidden="true" />
                  <p className="text-sm text-fieldiq-text">No tickets found.</p>
                  <p className="mt-1 text-xs text-fieldiq-muted">
                    {showArchived ? 'No archived tickets.' : 'Try adjusting your search or filter.'}
                  </p>
                </div>
              ) : (
                <>
                  {/* Wide viewports: the register as a tabular listing. */}
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full min-w-[980px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em]">
                        {/* The heading row carries the same 4px on its left edge
                            in no colour at all, so the columns line up with the
                            values under them. */}
                        <tr className="border-l-4 border-l-transparent">
                          <SortHead label="Ticket #" field="ticketNumber" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="w-[110px]" />
                          <SortHead label="Client" field="customer" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                          <SortHead label="Service Type" field="serviceType" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                          <SortHead label="Date" field="date" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                          <SortHead label="Hours" field="hours" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="!text-center" />
                          <SortHead label="Total" field="total" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="!text-right" />
                          <SortHead label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                          <th scope="col" className="w-[70px] px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedTickets.map((ticket) => {
                          const showEmail =
                            !!ticket.customerEmailStatus &&
                            ((ticket.status === 'office_approved' && !ticket.customerApprovedAt && !ticket.customerRejectionReason) ||
                              ['bounced', 'failed', 'complained'].includes(ticket.customerEmailStatus));
                          return (
                            <tr
                              key={ticket.id}
                              tabIndex={0}
                              onClick={() => openTicket(ticket.id)}
                              onKeyDown={rowKeys(ticket.id)}
                              className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${statusSkin(ticket.status).rail}`}
                            >
                              <td className="whitespace-nowrap px-4 py-3 text-sm font-extrabold tabular-nums text-fi-amber">
                                {ticket.ticketNumber || ticket.id.slice(0, 8)}
                              </td>
                              <td className="px-4 py-3 text-sm font-bold text-white">
                                {ticket.customer}
                                {showEmail && (
                                  <EmailStatusIndicator
                                    status={ticket.customerEmailStatus!}
                                    openCount={ticket.customerEmailOpenCount}
                                    lastOpenedAt={ticket.customerEmailLastOpenedAt}
                                  />
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1.5">
                                  {ticket.serviceType ? (
                                    <span className="whitespace-nowrap rounded-fi-control bg-fieldiq-dark px-2.5 py-1 text-xs font-medium text-fieldiq-text">
                                      {getJobTypeLabel(ticket.serviceType)}
                                    </span>
                                  ) : (
                                    <span className="text-sm text-fieldiq-muted">{JOB_TYPE_EMPTY_LABEL}</span>
                                  )}
                                  <ApprovalMark status={ticket.status} customerApprovedAt={ticket.customerApprovedAt} />
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-fieldiq-text">
                                {fmtDate(ticket.ticketDate)}
                                {ticket.arrivalDate && ticket.ticketDate && calendarDayString(ticket.arrivalDate) !== calendarDayString(ticket.ticketDate) && (
                                  <span className="block text-xs text-fieldiq-muted">Arrived {fmtDate(ticket.arrivalDate)}</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center text-sm">
                                <HoursDisplay hours={ticket.hours} contradictory={ticket.hoursContradictory} />
                              </td>
                              {/* The currency code goes THROUGH the formatter, so
                                  an absent total is a bare em dash rather than
                                  "— CAD". */}
                              <td className="px-4 py-3 text-right text-sm font-bold tabular-nums text-fieldiq-text">
                                {fmtCurrency(ticket.total, currency)}
                              </td>
                              <td className="px-4 py-3">
                                <TicketStatusBadge status={ticket.status} />
                              </td>
                              <td className="px-4 py-3">
                                <ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Narrow viewports: the same rows, stacked. No status rail
                      here — that 4px edge belongs to the surrounding panel.
                      These rows carry no controls of their own, so the whole
                      card stays one target. */}
                  <div className="space-y-3 p-4 md:hidden">
                    {sortedTickets.map((ticket) => {
                      const showEmail =
                        !!ticket.customerEmailStatus &&
                        ((ticket.status === 'office_approved' && !ticket.customerApprovedAt && !ticket.customerRejectionReason) ||
                          ['bounced', 'failed', 'complained'].includes(ticket.customerEmailStatus));
                      return (
                        <div
                          key={ticket.id}
                          tabIndex={0}
                          onClick={() => openTicket(ticket.id)}
                          onKeyDown={rowKeys(ticket.id)}
                          className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                        >
                          <div className="flex items-start justify-between gap-3 px-4 pb-3 pt-4">
                            <div className="min-w-0 flex-1">
                              <div className="mb-1.5 flex flex-wrap items-center gap-2">
                                <span className="text-sm font-extrabold text-fi-amber">
                                  {ticket.ticketNumber || ticket.id.slice(0, 8)}
                                </span>
                                <TicketStatusBadge status={ticket.status} />
                              </div>
                              <h3 className="truncate font-display text-base font-extrabold tracking-tight text-white">
                                {ticket.customer}
                                {showEmail && (
                                  <EmailStatusIndicator
                                    status={ticket.customerEmailStatus!}
                                    openCount={ticket.customerEmailOpenCount}
                                    lastOpenedAt={ticket.customerEmailLastOpenedAt}
                                  />
                                )}
                              </h3>
                              <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-sm text-fieldiq-text">
                                <span>
                                  {getJobTypeLabel(ticket.serviceType)} · {fmtDate(ticket.ticketDate)}
                                  {ticket.arrivalDate && ticket.ticketDate && calendarDayString(ticket.arrivalDate) !== calendarDayString(ticket.ticketDate) && (
                                    <span className="text-fieldiq-muted"> · arrived {fmtDate(ticket.arrivalDate)}</span>
                                  )}
                                </span>
                                <ApprovalMark status={ticket.status} customerApprovedAt={ticket.customerApprovedAt} />
                              </p>
                            </div>
                            <ChevronRight className="mt-1 size-4 flex-shrink-0 text-fieldiq-muted" aria-hidden="true" />
                          </div>
                          <div className="flex items-center gap-3 border-t border-fieldiq-border px-4 py-3">
                            <span className="flex items-center gap-1.5 text-sm">
                              <Clock className="size-3 text-fieldiq-muted" aria-hidden="true" />
                              <HoursDisplay hours={ticket.hours} contradictory={ticket.hoursContradictory} />
                            </span>
                            <span className="ml-auto text-sm font-bold tabular-nums text-fieldiq-text">
                              {fmtCurrency(ticket.total, currency)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </section>
          )}
        </div>
      </section>

      {/* The one-touch create, kept from before the re-skin.
          `right-4`, not the offset the first re-skinned pages carry: those were
          measured to clear a viewport-pinned agent button that no longer exists
          (fe#680), and nothing occupies that strip now. */}
      {!isSales && (
      <button
        type="button"
        onClick={() => navigate('/tickets/new')}
        aria-label="New ticket"
        className="fixed bottom-[var(--fab-bottom)] right-4 z-40 grid size-14 place-items-center rounded-fi-pill bg-fi-amber text-fi-amber-ink shadow-lg transition hover:brightness-105 active:scale-95"
      >
        <Plus className="size-6" aria-hidden="true" />
      </button>
      )}
    </div>
    </ErrorBoundary>
  );
}
